import Keycloak, { KeycloakLoginOptions, KeycloakProfile, KeycloakTokenParsed } from 'keycloak-js';
import { AppConfigService } from 'app/core/services/app-config/app-config.service';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { AuthStatus } from '../models/auth-status';
import { HttpHeaders } from '@angular/common/http';
import { JwtService } from './jwt.service';

@Injectable({
  	providedIn: 'root'
})

export class AuthService implements OnDestroy {

	private readonly EXPIRES_CHECK_INTERVAL = 240;  // 4 minutes.
	private readonly demoUserKeycloakProfile: KeycloakProfile = {
		id: 'DEMO_USER_ID',
		username: 'DemoUser',
		email: 'demouser@demo.com',
		firstName: 'DemoUser First Name',
		lastName: 'DemoUser Last Name',
		enabled: true,
		emailVerified: true
	};
	private keycloakLoginOptions: KeycloakLoginOptions = {
		redirectUri: window.location.origin + '/logged-in/'
	};

	private readonly keycloak!: Keycloak;
	private loginError = false;
	private refreshInterval: any;

	constructor(
		private configSvc: AppConfigService,
		private jwtService: JwtService
	) {
		if (!this.configSvc.settings.demoMode) {
			this.keycloak = inject(Keycloak);
		}
		this.startTokenRefreshInterval();
	}

	ngOnDestroy(): void {
		if (this.refreshInterval) { clearInterval(this.refreshInterval); }
	}

	private startTokenRefreshInterval(): void {
		if (this.configSvc.settings.demoMode) { return; }
		this.refreshInterval = setInterval(() => {
			if (this.getAuthenticationStatus() === AuthStatus.LOGGED_IN) {
				this.updateToken();
			}
		}, this.EXPIRES_CHECK_INTERVAL * 1000);
	}

	private updateToken(): void {
		if (this.configSvc.settings.demoMode) { return; }
		this.keycloak.updateToken(this.EXPIRES_CHECK_INTERVAL)
			.then(_refreshed => { })
			.catch(err => {
				throw new Error('[KeyCloak] Error refreshing token.', err);
			});
	}

	reachedLoggedInCallbackUrl(): void {
		if (this.configSvc.settings.demoMode) { return; }
		if (!this.keycloak.authenticated) {	this.loginError = true;	}
	}

	getAuthenticationStatus(): AuthStatus {
		if (this.configSvc.settings.demoMode) { return AuthStatus.LOGGED_IN; }
		if (this.loginError) {
			return AuthStatus.ERROR;
		} else if (this.keycloak.authenticated) {
			return AuthStatus.LOGGED_IN;
		} else {
			return AuthStatus.LOGGED_OUT;
		}
	}

	getRealmRoles(): string[] {
		if (this.configSvc.settings.demoMode) { return []; }
		return this.keycloak.realmAccess?.roles || [];
	}

	getResourceRoles(resourceName: string): string[] {
		if (this.configSvc.settings.demoMode) { return []; }
		return this.keycloak.resourceAccess?.[resourceName]?.roles || [];
	}

	checkResourceRolesIncludeAllRequiredRoles(resourceName: string, requiredRoles: string[]): boolean {
		if (this.configSvc.settings.demoMode) { return true; }
		return requiredRoles.every((role) => this.getResourceRoles(resourceName).includes(role));
	}

	checkResourceRolesIncludeAnyOfRequiredRoles(resourceName: string, requiredRoles: string[]): boolean {
		if (this.configSvc.settings.demoMode) { return true; }
		return requiredRoles.some((role) => this.getResourceRoles(resourceName).includes(role));
	}

	checkRealmRolesIncludeAllRequiredRoles(requiredRoles: string[]): boolean {
		if (this.configSvc.settings.demoMode) { return true; }
		return requiredRoles.every((role) => this.getRealmRoles().includes(role));
	}

	checkRealmRolesIncludeAnyOfRequiredRoles(requiredRoles: string[]): boolean {
		if (this.configSvc.settings.demoMode) { return true; }
		return requiredRoles.some((role) => this.getRealmRoles().includes(role));
	}

	getUserProfile(): Promise<KeycloakProfile> {
		if (this.configSvc.settings.demoMode) {
			return Promise.resolve(this.demoUserKeycloakProfile);
		}
		return this.keycloak.loadUserProfile();
	}

	getAllTokenFields(): KeycloakTokenParsed | undefined {
		if (this.configSvc.settings.demoMode) { return {}; }
		return this.keycloak.tokenParsed;
	}

	getTokenFieldVal(tokenFieldName: string): string | undefined {
		if (this.configSvc.settings.demoMode) { return 'DEMO_MODE'; }
		if(!tokenFieldName) { return ''; }
		let tmpFieldVal: KeycloakTokenParsed | undefined | any = this.getAllTokenFields();
		const tokenSubfieldNamesList = tokenFieldName.split('.');
		for (const tokenSubfieldName of tokenSubfieldNamesList) {
			if (!tmpFieldVal) { return undefined; }
			tmpFieldVal = tmpFieldVal[tokenSubfieldName];
		}
		return tmpFieldVal;
	}

	getAccessToken(): string | undefined {
		if (this.configSvc.settings.demoMode) { return this.configSvc.settings.demoToken; }
		return this.keycloak.token;
	}

	login(): void {
		if (this.configSvc.settings.demoMode) { return; }
		this.keycloak.login(this.keycloakLoginOptions);
	}

	logout(): void {
		if (this.configSvc.settings.demoMode) { return; }
		this.loginError = false;
		this.keycloak.logout();
	}

	addTokenToEndpointRequestHeaders(headers: HttpHeaders, url: string) {
		if (this.getAccessToken()) {
			headers = headers.set('Authorization', `Bearer ${this.getAccessToken()}`);
		}
		const token = this.jwtService.getToken();
		if (token) {
			if (url.includes('/public')) {
				headers = headers.set('Authorization', token);
			} else {
				headers = headers.set('Authorization', `Bearer ${token}`);
			}
		}
		return headers;
	}

	checkEndpointResponseError(error: any): void {
		if (!this.configSvc.settings.demoMode && (error.status === 401 || error.status === 403)) { this.login(); }
	}

}